import { useEffect, useState } from "react";
import { committeeApi } from "@/lib/api";
import { motion } from "framer-motion";
import { Sparkles, MapPin, CalendarDays, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  {
    key: "patron",
    title: "Patrons",
    subtitle: "Honorary leadership.",
    testId: "committee-section-patrons",
  },
  {
    key: "advisors",
    title: "Advisors",
    subtitle: "Guiding hands behind the conference.",
    testId: "committee-section-advisors",
  },
  {
    key: "leadership",
    title: "Core organising team",
    subtitle: "Running the conference, day-to-day.",
    testId: "committee-section-leadership",
  },
  {
    key: "organising_vice_chairs",
    title: "Organising Vice-Chairpersons",
    subtitle: "Vice-chairs of the organising committee.",
    testId: "committee-section-vice-chairs",
  },
  {
    key: "organising_secretary",
    title: "Organising Secretary",
    subtitle: "Lead secretariat of the conference.",
    testId: "committee-section-organising-secretary",
  },
  {
    key: "joint_secretaries",
    title: "Joint Secretaries",
    subtitle: "Supporting the organising secretary.",
    testId: "committee-section-joint-secretaries",
  },
  {
    key: "treasurer",
    title: "Treasurer",
    subtitle: "Steward of the conference finances.",
    testId: "committee-section-treasurer",
  },
  {
    key: "scientific",
    title: "Scientific programme",
    subtitle: "Curators of the science.",
    testId: "committee-section-scientific",
  },
  {
    key: "scientific_secretary",
    title: "Scientific Secretary",
    subtitle: "Leading the scientific programme.",
    testId: "committee-section-scientific-secretary",
  },
  {
    key: "scientific_asst_secretary",
    title: "Scientific Asst. Secretary",
    subtitle: "Supporting the scientific programme.",
    testId: "committee-section-scientific-asst",
  },
  {
    key: "medicine_update_editor",
    title: "Medicine Update Editor",
    subtitle: "Editing the medicine update publication.",
    testId: "committee-section-medicine-update",
  },
  {
    key: "pg_program_committee",
    title: "Post-Graduate Programme Committee",
    subtitle: "Championing the PG programme.",
    testId: "committee-section-pg-committee",
  },
  {
    key: "functional",
    title: "Functional chairs",
    subtitle: "The specialised working groups.",
    testId: "committee-section-functional",
  },
  {
    key: "other",
    title: "Also on the committee",
    subtitle: "Contributing members.",
    testId: "committee-section-other",
  },
];

const Section = ({ title, subtitle, members, testId }) => {
  if (!members.length) return null;
  return (
    <section className="mt-16" data-testid={testId}>
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
            {title}
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-apicon-navy sm:text-4xl">
            {subtitle}
          </h2>
        </div>
        <div className="hidden text-sm text-gray-500 sm:block">
          {members.length} member{members.length > 1 ? "s" : ""}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {members.map((m, i) => (
          <motion.article
            key={m.id || m.name}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: i * 0.04 }}
            className="group overflow-hidden rounded-3xl bg-white ring-1 ring-black/5 transition-shadow duration-300 hover:shadow-xl hover:shadow-apicon-navy/10"
            data-testid={`committee-card-${(m.name || "member").split(" ").pop().toLowerCase()}`}
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-apicon-mist">
              {m.img ? (
                <img
                  src={m.img}
                  alt={m.name}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-apicon-navy/30">
                  <Users className="h-16 w-16" />
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-apicon-navy/70 via-apicon-navy/10 to-transparent" />
              <div className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-apicon-navy shadow-sm backdrop-blur">
                {m.role}
              </div>
            </div>
            <div className="p-6">
              <div className="font-display text-xl font-semibold text-apicon-navy">
                {m.name}
              </div>
              {m.bio && (
                <p className="mt-3 text-sm leading-relaxed text-gray-600">
                  {m.bio}
                </p>
              )}
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
};

const Committee = () => {
  const [members, setMembers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    committeeApi
      .list()
      .then((list) => setMembers(Array.isArray(list) ? list : []))
      .catch(() => setMembers([]))
      .finally(() => setLoaded(true));
  }, []);

  const groupOf = (key) => members.filter((c) => (c.group || "leadership") === key);
  const isEmpty = loaded && members.length === 0;

  return (
    <div className="relative bg-white" data-testid="page-committee">
      {/* Header */}
      <section className="relative overflow-hidden bg-apicon-mist/50 py-20 md:py-24">
        <div className="pointer-events-none absolute inset-0 heritage-pattern opacity-60" />
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-apicon-gold/15 blur-3xl" />

        <div className="relative mx-auto grid w-full max-w-7xl gap-10 px-5 md:px-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
              Organising committee
            </div>
            <h1 className="mt-3 font-display text-5xl font-bold tracking-tight text-apicon-navy sm:text-6xl md:text-7xl">
              The Team{" "}
              <span className="italic text-apicon-green">Shaping</span>{" "}
              <br />
              the Journey.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-gray-600 sm:text-lg">
              A dedicated team of physicians, teachers and volunteers coming
              together to shape APICON 2026.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <span className="inline-flex items-center gap-2 text-apicon-navy">
                <CalendarDays className="h-4 w-4 text-apicon-gold" />
                <span className="font-medium">18 – 20 December, 2026</span>
              </span>
              <span className="inline-flex items-center gap-2 text-apicon-navy">
                <MapPin className="h-4 w-4 text-apicon-gold" />
                <span className="font-medium">
                  Tinsukia Medical College · Assam
                </span>
              </span>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="rounded-3xl border border-apicon-navy/10 bg-white p-6 shadow-sm">
              <Sparkles className="h-6 w-6 text-apicon-gold" />
              <div className="mt-4 font-display text-lg font-semibold text-apicon-navy">
                Reach the secretariat
              </div>
              <p className="mt-2 text-sm text-gray-600">
                For committee introductions, faculty invitations or delegate
                queries — write to us at{" "}
                <a
                  href="mailto:assamapicon2026tinsukia@gmail.com"
                  className="font-medium text-apicon-navy underline underline-offset-4"
                >
                  assamapicon2026tinsukia@gmail.com
                </a>
                .
              </p>
              <Link
                to="/register"
                className="mt-6 block"
                data-testid="committee-header-register-cta"
              >
                <Button className="w-full rounded-full bg-apicon-gold font-semibold text-apicon-navy hover:bg-[#ffb400]">
                  Become a delegate
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sections by tier */}
      <section className="relative bg-white pb-24">
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          {isEmpty ? (
            <div
              className="mt-16 rounded-3xl border border-dashed border-apicon-navy/20 bg-apicon-mist/40 px-8 py-16 text-center"
              data-testid="committee-empty-state"
            >
              <Users className="mx-auto h-10 w-10 text-apicon-navy/40" />
              <div className="mt-4 font-display text-2xl font-semibold text-apicon-navy">
                Committee will be announced soon.
              </div>
              <p className="mt-3 text-sm text-gray-600">
                Please check back — we&rsquo;re finalising the line-up.
              </p>
            </div>
          ) : (
            SECTIONS.map((s) => (
              <Section
                key={s.key}
                title={s.title}
                subtitle={s.subtitle}
                members={groupOf(s.key)}
                testId={s.testId}
              />
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default Committee;
